import { useState, useEffect } from "react";
import api, { formatError } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  Users, Stethoscope, Calendar, CreditCard, TrendingUp,
  FlaskConical, Activity, DollarSign, Download
} from "lucide-react";

const STATUS_COLORS = {
  waiting: "#f59e0b",
  in_consultation: "#3b82f6",
  completed: "#10b981",
  cancelled: "#f43f5e",
  unknown: "#94a3b8",
};

const GENDER_COLORS = ["#3b82f6", "#ec4899", "#94a3b8"];
const VISIT_COLORS = ["#059669", "#f59e0b", "#6366f1", "#14b8a6", "#ef4444"];

const RANGES = [
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];

export default function Analytics() {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const activePeriod = customStart && customEnd
    ? { label: `${customStart} → ${customEnd}`, days: null }
    : { label: `${days}d`, days };

  useEffect(() => {
    setLoading(true);
    const url = customStart && customEnd
      ? `/analytics/overview?start=${customStart}&end=${customEnd}`
      : `/analytics/overview?days=${days}`;
    api.get(url)
      .then(r => setData(r.data))
      .catch(err => toast({ title: "Error", description: formatError(err), variant: "destructive" }))
      .finally(() => setLoading(false));
  }, [days, customStart, customEnd, toast]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [["Date", "Appointments", "Revenue"]];
    data.daily.forEach(d => rows.push([d.date, d.appointments, d.revenue]));
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `analytics-${days}d-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-emerald-700 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { totals, daily, status_breakdown, top_doctors, gender_split, visit_types } = data;

  const statCards = [
    { label: "Total Patients", value: totals.patients, icon: Users, color: "text-violet-600", bg: "bg-violet-50" },
    { label: "Total Doctors", value: totals.doctors, icon: Stethoscope, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Appointments", value: totals.appointments, icon: Calendar, color: "text-blue-600", bg: "bg-blue-50" },
    { label: `Revenue (${activePeriod.label})`, value: `₹${totals.revenue_period.toLocaleString()}`, icon: TrendingUp, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Total Revenue", value: `₹${totals.total_revenue.toLocaleString()}`, icon: DollarSign, color: "text-teal-600", bg: "bg-teal-50" },
    { label: "Paid Bills", value: totals.paid_bills, icon: CreditCard, color: "text-rose-600", bg: "bg-rose-50" },
    { label: "Pending Labs", value: totals.pending_labs, icon: FlaskConical, color: "text-indigo-600", bg: "bg-indigo-50" },
    { label: "Total Bills", value: totals.bills, icon: Activity, color: "text-slate-600", bg: "bg-slate-100" },
  ];

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-sans">Analytics & Reports</h1>
          <p className="text-slate-500 text-sm mt-1">Clinic performance insights — <span className="font-medium text-slate-700">{activePeriod.label}</span></p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center bg-slate-100 rounded-lg p-1 gap-1">
            {RANGES.map(r => (
              <Button
                key={r.days}
                variant="ghost"
                size="sm"
                onClick={() => { setCustomStart(""); setCustomEnd(""); setShowCustom(false); setDays(r.days); }}
                className={!customStart && days === r.days ? "bg-white shadow-sm text-slate-900 h-8 px-3" : "h-8 px-3 text-slate-600 hover:bg-white"}
                data-testid={`range-${r.days}`}
              >
                {r.label}
              </Button>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCustom(!showCustom)}
              className={customStart ? "bg-white shadow-sm text-slate-900 h-8 px-3" : "h-8 px-3 text-slate-600 hover:bg-white"}
              data-testid="range-custom"
            >
              Custom
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={exportCSV} className="gap-1.5" data-testid="export-csv-btn">
            <Download size={14} /> CSV
          </Button>
        </div>
      </div>

      {showCustom && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-end gap-3 flex-wrap" data-testid="custom-range-panel">
          <div>
            <label className="text-xs font-medium text-slate-700 mb-1 block">From</label>
            <input
              type="date"
              value={customStart}
              max={customEnd || undefined}
              onChange={e => setCustomStart(e.target.value)}
              className="border border-slate-200 rounded-md px-3 py-1.5 text-sm h-9"
              data-testid="custom-start-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 mb-1 block">To</label>
            <input
              type="date"
              value={customEnd}
              min={customStart || undefined}
              max={new Date().toISOString().split("T")[0]}
              onChange={e => setCustomEnd(e.target.value)}
              className="border border-slate-200 rounded-md px-3 py-1.5 text-sm h-9"
              data-testid="custom-end-input"
            />
          </div>
          {customStart && customEnd && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setCustomStart(""); setCustomEnd(""); setShowCustom(false); }}
              className="h-9 text-xs text-slate-500 hover:text-slate-900"
              data-testid="clear-custom-range"
            >
              Clear
            </Button>
          )}
          <p className="text-xs text-slate-500 ml-auto">Max 365 days range. End date inclusive.</p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
            <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
              <Icon size={18} className={color} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-slate-500 truncate">{label}</p>
              <p className={`text-lg font-bold font-sans ${color} truncate`}>{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Revenue + Appointments Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 text-sm font-sans">Daily Appointments & Revenue</h3>
            <span className="text-xs text-slate-500">{activePeriod.label}</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: "#64748b" }}
                tickFormatter={(d) => d?.slice(5)}
                interval={Math.floor(daily.length / 10)}
              />
              <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#64748b" }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#64748b" }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line yAxisId="left" type="monotone" dataKey="appointments" stroke="#059669" strokeWidth={2} dot={false} name="Appointments" />
              <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#f59e0b" strokeWidth={2} dot={false} name="Revenue (₹)" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Appointment Status Breakdown */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 text-sm font-sans mb-4">Appointment Status</h3>
          {status_breakdown.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">No data</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={status_breakdown} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={60} innerRadius={35}>
                    {status_breakdown.map((s, i) => (
                      <Cell key={i} fill={STATUS_COLORS[s.status] || "#94a3b8"} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-3">
                {status_breakdown.map(s => (
                  <div key={s.status} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS[s.status] || "#94a3b8" }} />
                      <span className="text-slate-600 capitalize">{s.status.replace("_", " ")}</span>
                    </div>
                    <span className="font-semibold text-slate-900">{s.count}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Top Doctors + Demographics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 text-sm font-sans mb-4">Top 5 Doctors by Appointments</h3>
          {top_doctors.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">No appointments yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={top_doctors} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: "#64748b" }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#334155" }}
                  width={140}
                  tickFormatter={(v) => v?.length > 20 ? v.slice(0, 18) + "…" : v}
                />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="count" fill="#059669" radius={[0, 4, 4, 0]} name="Appointments" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 text-sm font-sans mb-4">Patient Demographics</h3>
          {gender_split.every(g => g.value === 0) ? (
            <div className="text-center py-10 text-slate-400 text-sm">No patient data</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={gender_split.filter(g => g.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60}>
                    {gender_split.filter(g => g.value > 0).map((_, i) => (
                      <Cell key={i} fill={GENDER_COLORS[i % GENDER_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-3">
                {gender_split.filter(g => g.value > 0).map((g, i) => (
                  <div key={g.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: GENDER_COLORS[i % GENDER_COLORS.length] }} />
                      <span className="text-slate-600 capitalize">{g.name}</span>
                    </div>
                    <span className="font-semibold text-slate-900">{g.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Visit Types */}
      {visit_types.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 text-sm font-sans mb-4">Visit Type Breakdown</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={visit_types}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#334155" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} name="Count">
                {visit_types.map((_, i) => (
                  <Cell key={i} fill={VISIT_COLORS[i % VISIT_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Doctor Earnings */}
      <DoctorEarnings period={{ days, customStart, customEnd }} />
    </div>
  );
}

function DoctorEarnings({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = period.customStart && period.customEnd
      ? `/analytics/doctor-earnings?start=${period.customStart}&end=${period.customEnd}`
      : `/analytics/doctor-earnings?days=${period.days}`;
    api.get(url)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period.days, period.customStart, period.customEnd]);

  if (loading) return null;
  if (!data || data.doctors.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-900 text-sm font-sans mb-4">Doctor Earnings</h3>
        <div className="text-center py-10 text-slate-400 text-sm">
          <p>No doctor-linked revenue in this period.</p>
          <p className="text-xs mt-1">Bills linked to appointments will appear here.</p>
        </div>
      </div>
    );
  }

  const maxRev = Math.max(...data.doctors.map(d => d.revenue), 1);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-5" data-testid="doctor-earnings-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold text-slate-900 text-sm font-sans">Doctor Earnings</h3>
        <div className="text-xs text-slate-500">Total <span className="font-bold text-emerald-700">₹{data.total_revenue.toLocaleString()}</span> across {data.doctors.length} doctor{data.doctors.length !== 1 ? "s" : ""}</div>
      </div>

      {/* Per-doctor breakdown */}
      <div className="space-y-2">
        {data.doctors.map((doc, i) => {
          const pct = (doc.revenue / maxRev) * 100;
          return (
            <div key={doc.doctor_name || i} className="space-y-1" data-testid={`doc-earning-row-${i}`}>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-bold text-slate-400 w-5">#{i + 1}</span>
                  <span className="font-medium text-slate-900 truncate">{doc.doctor_name}</span>
                  <span className="text-xs text-slate-500">· {doc.bill_count} bill{doc.bill_count !== 1 ? "s" : ""}</span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {doc.insurance_amount > 0 && (
                    <span className="text-[10px] text-blue-600" title="Insurance covered">₹{doc.insurance_amount.toLocaleString()} ins</span>
                  )}
                  <span className="font-bold text-emerald-700 font-sans">₹{doc.revenue.toLocaleString()}</span>
                </div>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Monthly matrix */}
      {data.months.length > 0 && (
        <div className="pt-4 border-t border-slate-100 overflow-x-auto">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Monthly Revenue Matrix</p>
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left p-2 font-medium text-slate-600 text-xs">Doctor</th>
                {data.months.map(m => (
                  <th key={m} className="text-right p-2 font-medium text-slate-600 text-xs">
                    {new Date(`${m}-01T00:00:00Z`).toLocaleString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.doctors.map((doc, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="p-2 text-slate-800 font-medium text-xs truncate max-w-32">{doc.doctor_name}</td>
                  {data.months.map(m => {
                    const cell = data.monthly_by_doctor[doc.doctor_name]?.[m];
                    return (
                      <td key={m} className="text-right p-2">
                        {cell ? (
                          <div>
                            <div className="text-xs font-semibold text-emerald-700">₹{cell.revenue.toLocaleString()}</div>
                            <div className="text-[10px] text-slate-400">{cell.count} bill{cell.count !== 1 ? "s" : ""}</div>
                          </div>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
